import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGem, faCheck, faSpinner, faExclamationTriangle } from '@/icons';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { useAuthContext } from '../contexts/AuthContext';
import { enabledProFeatures } from '../constants/proFeatureCatalog';
import { teamApiService } from '../services/teamApiService';
import { BackgroundSaveService } from '../services/backgroundSaveService';
import { alertManager } from '../services/AlertManager';

export interface ProTrialSelfSignupViewProps {
    /** How long the trial runs, from the server so this never names its own number. */
    weeks: number;
    /** Back to the upgrade flow. */
    onCancel: () => void;
}

/**
 * What a team sees before giving itself a Pro trial: everything Pro turns on, and what happens
 * when the trial ends.
 *
 * Descriptions only, no demo videos -- someone deciding whether to start a trial wants the list,
 * not a tour, and the videos live in ProFeatureTourModal for people who want one.
 *
 * The consequences below are stated because they are real and arrive later, when nobody is
 * reading a modal: the whole team is signed out the moment this starts, and when the trial ends
 * the cases that used Pro features stop being editable. Both are recoverable and the copy says
 * so, but neither should be a surprise.
 */
export const ProTrialSelfSignupView: React.FC<ProTrialSelfSignupViewProps> = ({ weeks, onCancel }) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags } = useFeatureFlagContext();
    const { logout } = useAuthContext();

    const [starting, setStarting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const features = enabledProFeatures(flags);

    const handleStart = async () => {
        if (starting) return;
        setStarting(true);
        setError(null);

        try {
            // The whole team is force-logged-out by this call, so anything still sitting in a
            // page-local debounce has to reach the server on the token that is still valid --
            // the same reason the paid upgrade and the trial downgrade both flush first.
            await BackgroundSaveService.flush();

            const result = await teamApiService.startSelfServiceTrial();
            if (!result.success) {
                setError(result.message || 'Your Pro trial could not be started.');
                setStarting(false);
                return;
            }

            alertManager.addAlert('success', 'Your Pro trial has started. Sign in again to pick up Pro features.');
            logout();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Your Pro trial could not be started.');
            setStarting(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Everything above the buttons scrolls. On a short viewport the buttons would
                otherwise sit below the fold, and the one thing the user came here to press would
                be the one thing they have to go looking for. */}
            <div style={{
                flex: '1 1 auto',
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.md,
                paddingRight: theme.spacing.xs,
            }}>
            <div>
                <div style={{
                    ...themedStyles.text.body,
                    fontSize: theme.typography.fontSize.md,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.colors.text.primary,
                }}>
                    Your free {weeks}-week trial turns all of this on:
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                {features.map((feature) => (
                    <div key={feature.key} style={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing.sm }}>
                        <FontAwesomeIcon
                            icon={faCheck}
                            style={{ color: theme.colors.primary, fontSize: '11px', flexShrink: 0, marginTop: '5px' }}
                        />
                        <div style={{ minWidth: 0 }}>
                            <div style={{
                                ...themedStyles.text.body,
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.semibold,
                                color: theme.colors.text.primary,
                            }}>
                                {feature.label}
                            </div>
                            <div style={{
                                ...themedStyles.text.body,
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.secondary,
                            }}>
                                {feature.description}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div style={{
                ...themedStyles.text.body,
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.text.secondary,
                padding: theme.spacing.md,
                borderRadius: theme.borderRadius.md,
                backgroundColor: theme.colors.surfaceSecondary,
                border: `1px solid ${theme.colors.border.default}`,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.sm,
            }}>
                <div>
                    The trial is free and temporary. It costs nothing, there is no card to enter,
                    and nothing is charged when it ends.
                </div>
                <div>
                    When the {weeks} weeks are up, a Calculist representative will contact your
                    team so you can decide whether you want to stay at Pro or go back to the
                    Standard level.
                </div>
                <div>
                    If you go back to Standard, any case that used a Pro feature becomes{' '}
                    <strong>read only</strong> — you can still open it,
                    download it and copy it. Copying removes the Pro-only data, so the copy is
                    yours to edit again. Nothing is deleted.
                </div>
                <div>
                    Starting the trial signs out everyone on your team, including you. Sign back in
                    and Pro is on.
                </div>
            </div>

            {error && (
                <div style={{
                    ...themedStyles.text.body,
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.colors.status.error,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing.xs,
                }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginTop: '3px' }} />
                    <span>{error}</span>
                </div>
            )}

            </div>

            <div style={{
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                gap: theme.spacing.md,
                flexWrap: 'wrap',
                paddingTop: theme.spacing.md,
                marginTop: theme.spacing.md,
                borderTop: `1px solid ${theme.colors.border.default}`,
                backgroundColor: theme.colors.surface,
            }}>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={starting}
                    style={{
                        padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                        border: `1px solid ${theme.colors.border.default}`,
                        borderRadius: theme.borderRadius.md,
                        background: 'transparent',
                        color: theme.colors.text.primary,
                        cursor: starting ? 'not-allowed' : 'pointer',
                        fontSize: theme.typography.fontSize.sm,
                        fontWeight: theme.typography.fontWeight.medium,
                        opacity: starting ? 0.6 : 1,
                    }}
                >
                    Back
                </button>

                <button
                    type="button"
                    onClick={handleStart}
                    disabled={starting}
                    style={{
                        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                        background: theme.colors.primary,
                        color: theme.colors.text.inverse,
                        border: 'none',
                        borderRadius: theme.borderRadius.md,
                        cursor: starting ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing.sm,
                        fontSize: theme.typography.fontSize.lg,
                        fontWeight: theme.typography.fontWeight.semibold,
                        opacity: starting ? 0.7 : 1,
                    }}
                >
                    <FontAwesomeIcon icon={starting ? faSpinner : faGem} spin={starting} />
                    {starting ? 'Starting your trial…' : 'Start Your Free Trial'}
                </button>
            </div>
        </div>
    );
};

export default ProTrialSelfSignupView;
